import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MinusIcon, PlusIcon, TruckIcon, ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { wilayas, getCommunes } from '../data/algeriaLocations';
import JeansSizeAnimation from '../components/JeansSizeAnimation';
import type { Product } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '';

const getImageUrl = (path: string) => {
  if (path.startsWith('http')) return path;
  return `${API_BASE}${path}`;
};

interface ProductFormState {
  selectedSize: string;
  selectedColor: string;
  quantity: number;
  fullName: string;
  phoneNumber: string;
  wilaya: string;
  commune: string;
  address: string;
  submitting: boolean;
  imageIndex: number;
}

const SIZE_GUIDE = [
  { size: '31', cm: '38' },
  { size: '32', cm: '40' },
  { size: '34', cm: '42' },
  { size: '36', cm: '44' },
  { size: '38', cm: '46' },
  { size: '40', cm: '48' },
];

const AdsLanding = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [formStates, setFormStates] = useState<Record<string, ProductFormState>>({});

  const [lightboxOpen, setLightboxOpen] = useState(false);

  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const lightboxPrev = useCallback(() => {
    const p = products[activeIndex];
    if (!p) return;
    const cur = formStates[p._id]?.imageIndex ?? 0;
    updateForm(p._id, { imageIndex: (cur - 1 + p.images.length) % p.images.length });
  }, [products, activeIndex, formStates]);

  const lightboxNext = useCallback(() => {
    const p = products[activeIndex];
    if (!p) return;
    const cur = formStates[p._id]?.imageIndex ?? 0;
    updateForm(p._id, { imageIndex: (cur + 1) % p.images.length });
  }, [products, activeIndex, formStates]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') lightboxPrev();
      else if (e.key === 'ArrowRight') lightboxNext();
      else if (e.key === 'Escape') setLightboxOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen, lightboxPrev, lightboxNext]);

  useEffect(() => {
    fetch('/api/tracking', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'ad_view' }),
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await fetch('/api/products?limit=100');
        const data = await res.json();
        if (data.success) {
          const active = (data.data.products || []).filter((p: Product) => p.isActive);
          setProducts(active);
          const states: Record<string, ProductFormState> = {};
          for (const p of active) {
            states[p._id] = {
              selectedSize: p.sizes[0] || '',
              selectedColor: p.colors[0] || '',
              quantity: 1,
              fullName: '',
              phoneNumber: '',
              wilaya: '',
              commune: '',
              address: '',
              submitting: false,
              imageIndex: 0,
            };
          }
          setFormStates(states);
        }
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Meta Pixel — ViewContent fires whenever the viewed product changes
  useEffect(() => {
    const p = products[activeIndex];
    if (!p || !window.fbq) return;
    window.fbq('track', 'ViewContent', {
      content_ids: [p._id],
      content_name: p.name,
      content_type: 'product',
      value: p.salePrice || p.price,
      currency: 'DZD',
    });
  }, [products, activeIndex]);

  const updateForm = (productId: string, updates: Partial<ProductFormState>) => {
    setFormStates(prev => ({
      ...prev,
      [productId]: { ...prev[productId], ...updates },
    }));
  };

  const goTo = (index: number) => {
    setActiveIndex(Math.max(0, Math.min(products.length - 1, index)));
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    const p = products[activeIndex];
    if (!p || p.images.length <= 1) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      const cur = formStates[p._id]?.imageIndex ?? 0;
      if (diff > 0) {
        updateForm(p._id, { imageIndex: (cur + 1) % p.images.length });
      } else {
        updateForm(p._id, { imageIndex: (cur - 1 + p.images.length) % p.images.length });
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent, product: Product) => {
    e.preventDefault();
    const form = formStates[product._id];
    if (!form) return;

    updateForm(product._id, { submitting: true });

    try {
      let formattedPhone = form.phoneNumber.replace(/\D/g, '');
      if (formattedPhone.startsWith('213')) {
        formattedPhone = '+' + formattedPhone;
      } else if (formattedPhone.startsWith('0')) {
        formattedPhone = '+213' + formattedPhone.slice(1);
      } else if (formattedPhone.length === 9 && /^[5-7]/.test(formattedPhone)) {
        formattedPhone = '+213' + formattedPhone;
      } else {
        formattedPhone = '+213' + formattedPhone;
      }

      if (!/^(\+213)[5-7][0-9]{8}$/.test(formattedPhone)) {
        alert('Invalid phone number. Example: 0555 123 456');
        updateForm(product._id, { submitting: false });
        return;
      }

      // Track order button click
      fetch('/api/tracking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: 'ad_click', productId: product._id, productName: product.name }),
      }).catch(() => {});

      const itemPrice = product.salePrice || product.price;
      const subtotal = itemPrice * form.quantity;
      const deliveryFee = product.freeDelivery ? 0 : (subtotal >= 8000 ? 0 : 500);
      const total = subtotal + deliveryFee;

      const orderData = {
        customer: {
          name: form.fullName,
          phone: formattedPhone,
          state: form.wilaya,
          city: form.commune,
          address: form.address,
        },
        items: [{
          product: product._id,
          size: form.selectedSize,
          quantity: form.quantity,
          price: itemPrice,
        }],
        subtotal,
        deliveryFee,
        total,
        notes: `Size: ${form.selectedSize}${form.selectedColor ? `, Color: ${form.selectedColor}` : ''} — via Ads`,
      };

      const response = await fetch('/api/orders', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(orderData),
      });
      const responseData = await response.json();

      if (response.ok && responseData.success) {
        if (window.fbq) {
          window.fbq('track', 'Purchase', {
            value: total,
            currency: 'DZD',
            content_ids: [product._id],
            content_name: product.name,
            content_type: 'product',
            num_items: form.quantity,
          });
        }
        const orderNumber = responseData.data?.orderNumber || '';
        navigate(`/ads/success?order=${orderNumber}&product=${encodeURIComponent(product.name)}`);
      } else {
        if (responseData.errors?.length) {
          alert(responseData.errors.map((err: { msg: string }) => err.msg).join('\n'));
        } else {
          alert(responseData.message || 'Failed to submit order. Please try again.');
        }
        updateForm(product._id, { submitting: false });
      }
    } catch {
      alert('An error occurred. Please try again.');
      updateForm(product._id, { submitting: false });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-offwhite flex items-center justify-center">
        <div className="spinner-lg" />
      </div>
    );
  }

  const product = products[activeIndex];
  const form = product ? formStates[product._id] : null;

  return (
    <div className="bg-offwhite min-h-screen">
      {/* Header */}
      <header className="bg-offblack py-4 text-center">
        <img
          src="/arme logo.png"
          alt="armé"
          className="w-20 h-20 mx-auto"
          style={{ filter: 'invert(1) grayscale(1) brightness(2)', mixBlendMode: 'screen' }}
        />
        <p className="text-warmgray-400 text-xs tracking-[0.3em] uppercase mt-1">Premium Jeans & Shoes</p>
      </header>

      {/* Product panel */}
      {product && form && (
        <div className="max-w-2xl mx-auto px-4 py-8">
          <div className="bg-white border border-warmgray-200 overflow-hidden">

            {/* ── Image Gallery ── */}
            <div className="space-y-3 p-3">
              {/* Main image */}
              <div
                className="relative aspect-[3/4] bg-warmgray-100 overflow-hidden cursor-zoom-in"
                onClick={() => setLightboxOpen(true)}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                <img
                  key={`${product._id}-${form.imageIndex}`}
                  src={getImageUrl(product.images[form.imageIndex] || product.images[0])}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-500"
                />
                {product.salePrice && product.salePrice > 0 && (
                  <span className="absolute top-4 left-4 badge-sale">
                    -{Math.round(((product.price - product.salePrice) / product.price) * 100)}%
                  </span>
                )}
                {product.images.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); updateForm(product._id, { imageIndex: (form.imageIndex - 1 + product.images.length) % product.images.length }); }}
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
                      aria-label="Previous image"
                    >
                      <ChevronLeftIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); updateForm(product._id, { imageIndex: (form.imageIndex + 1) % product.images.length }); }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
                      aria-label="Next image"
                    >
                      <ChevronRightIcon className="w-5 h-5" />
                    </button>
                    {/* Dots — mobile */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 md:hidden">
                      {product.images.map((_, idx) => (
                        <span
                          key={idx}
                          className={`block w-1.5 h-1.5 rounded-full transition-colors ${idx === form.imageIndex ? 'bg-offblack' : 'bg-offblack/30'}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnails — desktop */}
              {product.images.length > 1 && (
                <div className="hidden md:flex gap-2 overflow-x-auto">
                  {product.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => updateForm(product._id, { imageIndex: idx })}
                      className={`shrink-0 w-20 h-24 bg-warmgray-100 overflow-hidden border-2 transition-colors ${
                        idx === form.imageIndex
                          ? 'border-offblack'
                          : 'border-transparent hover:border-warmgray-300'
                      }`}
                    >
                      <img src={getImageUrl(img)} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="p-5 space-y-6">
              {/* ── Product Info ── */}
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-warmgray-400 mb-1">{product.brand}</p>
                <h2 className="font-display text-2xl text-offblack tracking-tight">{product.name}</h2>
                <div className="flex items-baseline gap-3 mt-2">
                  <span className="font-display text-xl text-offblack">
                    {(product.salePrice || product.price).toLocaleString()} DA
                  </span>
                  {product.salePrice && product.salePrice > 0 && (
                    <span className="text-warmgray-400 line-through text-sm">{product.price.toLocaleString()} DA</span>
                  )}
                </div>
                {product.freeDelivery && (
                  <div className="inline-flex items-center gap-2 mt-3 bg-gradient-to-r from-green-600 to-emerald-500 text-white px-3 py-1.5 text-xs font-semibold tracking-wide uppercase">
                    <TruckIcon className="w-3.5 h-3.5 shrink-0" />
                    Free Delivery
                  </div>
                )}
              </div>

              <div className="h-px bg-warmgray-200" />

              {/* ── Product Selector (image thumbnails) ── */}
              {products.length > 1 && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-offblack mb-3">Product</label>
                  <div className="flex flex-wrap gap-3">
                    {products.map((p, i) => (
                      <button
                        key={p._id}
                        onClick={() => goTo(i)}
                        className="flex flex-col items-center gap-1.5 group"
                      >
                        <div className={`w-16 h-20 overflow-hidden border-2 transition-all ${
                          i === activeIndex
                            ? 'border-offblack'
                            : 'border-transparent hover:border-warmgray-300'
                        }`}>
                          <img
                            src={getImageUrl(p.images[0])}
                            alt={p.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className={`text-[0.6rem] uppercase tracking-wide font-medium leading-tight text-center max-w-[64px] ${
                          i === activeIndex ? 'text-offblack' : 'text-warmgray-400 group-hover:text-offblack'
                        }`}>
                          {p.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Color Selection ── */}
              {product.colors.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-xs font-semibold uppercase tracking-wider text-offblack">Color</label>
                    <span className="text-xs text-warmgray-500">{form.selectedColor}</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {product.colors.map((color) => (
                      <button
                        key={color}
                        onClick={() => updateForm(product._id, { selectedColor: color })}
                        className={`px-4 h-11 border text-sm font-medium transition-all ${
                          form.selectedColor === color
                            ? 'border-offblack bg-offblack text-offwhite'
                            : 'border-warmgray-300 text-offblack hover:border-offblack'
                        }`}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Size Selection + Guide ── */}
              {product.sizes.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-xs font-semibold uppercase tracking-wider text-offblack">Size</label>
                    <span className="text-xs text-warmgray-500">{form.selectedSize}</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {product.sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => updateForm(product._id, { selectedSize: size })}
                        className={`min-w-[44px] h-11 px-4 border text-sm font-medium transition-all ${
                          form.selectedSize === size
                            ? 'border-offblack bg-offblack text-offwhite'
                            : 'border-warmgray-300 text-offblack hover:border-offblack'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>

                  {/* Size Guide */}
                  <div className="border border-warmgray-200 bg-warmgray-50">
                    <p className="px-4 pt-3 pb-2 text-xs uppercase tracking-[0.15em] text-warmgray-500 font-medium">
                      Size Guide
                    </p>
                    <div className="px-4 pb-2">
                      <JeansSizeAnimation />
                    </div>
                    <div className="px-4 pb-4">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-offblack text-offwhite">
                            <th className="py-1.5 px-2 text-left font-semibold tracking-wide">Size</th>
                            <th className="py-1.5 px-2 text-left font-semibold tracking-wide">Waist (cm)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {SIZE_GUIDE.map((row, i) => (
                            <tr
                              key={row.size}
                              className={`border-t border-warmgray-200 ${
                                form.selectedSize === row.size
                                  ? 'bg-offblack text-offwhite font-semibold'
                                  : i % 2 === 0 ? 'bg-white' : 'bg-warmgray-50'
                              }`}
                            >
                              <td className="py-1.5 px-2">{row.size}</td>
                              <td className="py-1.5 px-2">{row.cm} cm</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Quantity ── */}
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-offblack mb-3">Quantity</label>
                <div className="inline-flex items-center border border-warmgray-300">
                  <button
                    onClick={() => updateForm(product._id, { quantity: Math.max(1, form.quantity - 1) })}
                    className="w-11 h-11 flex items-center justify-center hover:bg-warmgray-100 transition-colors"
                    aria-label="Decrease"
                  >
                    <MinusIcon className="w-4 h-4" />
                  </button>
                  <span className="w-11 h-11 flex items-center justify-center text-sm font-medium border-x border-warmgray-300">
                    {form.quantity}
                  </span>
                  <button
                    onClick={() => updateForm(product._id, { quantity: Math.min(product.stock || 10, form.quantity + 1) })}
                    className="w-11 h-11 flex items-center justify-center hover:bg-warmgray-100 transition-colors"
                    aria-label="Increase"
                  >
                    <PlusIcon className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <span className={`w-2 h-2 rounded-full ${product.stock > 0 ? 'bg-olive' : 'bg-warmgray-400'}`} />
                  <span className="text-xs text-warmgray-500">
                    {product.stock > 0 ? 'In stock' : 'Out of stock'}
                  </span>
                </div>
              </div>

              <div className="h-px bg-warmgray-200" />

              {/* ── Order Form ── */}
              <form onSubmit={(e) => handleSubmit(e, product)} className="space-y-4">
                <p className="text-xs uppercase tracking-[0.2em] text-warmgray-500 font-semibold">
                  Delivery Information
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-offblack mb-1.5">Full Name *</label>
                    <input
                      type="text"
                      required
                      value={form.fullName}
                      onChange={(e) => updateForm(product._id, { fullName: e.target.value })}
                      className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors placeholder:text-warmgray-300"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-offblack mb-1.5">Phone Number *</label>
                    <input
                      type="tel"
                      required
                      value={form.phoneNumber}
                      onChange={(e) => updateForm(product._id, { phoneNumber: e.target.value })}
                      className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors placeholder:text-warmgray-300"
                      placeholder="0555 123 456"
                    />
                    <p className="text-[0.65rem] text-warmgray-400 mt-1">05 / 06 / 07 ···</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-offblack mb-1.5">Wilaya *</label>
                    <select
                      required
                      value={form.wilaya}
                      onChange={(e) => updateForm(product._id, { wilaya: e.target.value, commune: '' })}
                      className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack bg-white"
                    >
                      <option value="">Select wilaya</option>
                      {wilayas.map((w) => <option key={w} value={w}>{w}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-offblack mb-1.5">Commune *</label>
                    <select
                      required
                      value={form.commune}
                      onChange={(e) => updateForm(product._id, { commune: e.target.value })}
                      className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack bg-white disabled:opacity-40"
                      disabled={!form.wilaya}
                    >
                      <option value="">{form.wilaya ? 'Select commune' : '—'}</option>
                      {getCommunes(form.wilaya).map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-offblack mb-1.5">Address *</label>
                  <textarea
                    required
                    value={form.address}
                    onChange={(e) => updateForm(product._id, { address: e.target.value })}
                    className="w-full border border-warmgray-200 px-3 py-2.5 text-sm text-offblack focus:outline-none focus:border-offblack transition-colors resize-none placeholder:text-warmgray-300"
                    rows={2}
                    placeholder="Street, building, floor..."
                  />
                </div>

                {/* Order Total */}
                {(() => {
                  const itemPrice = product.salePrice || product.price;
                  const subtotal = itemPrice * form.quantity;
                  const deliveryFee = product.freeDelivery ? 0 : (subtotal >= 8000 ? 0 : 500);
                  const total = subtotal + deliveryFee;
                  return (
                    <div className="bg-offblack px-4 py-3 flex items-center justify-between">
                      <div>
                        <p className="text-[0.6rem] uppercase tracking-widest text-warmgray-400">Order Total</p>
                        <p className="text-xs text-warmgray-400 mt-0.5">
                          {form.quantity} item{form.quantity > 1 ? 's' : ''} ·{' '}
                          {deliveryFee === 0 ? 'Free delivery' : `+${deliveryFee} DA delivery`}
                        </p>
                      </div>
                      <p className="font-display text-2xl text-offwhite">
                        {total.toLocaleString()} <span className="text-sm text-warmgray-400">DA</span>
                      </p>
                    </div>
                  );
                })()}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={form.submitting || product.stock === 0}
                  className="w-full bg-offblack text-offwhite py-4 text-sm font-semibold tracking-[0.12em] uppercase hover:bg-charcoal transition-colors disabled:opacity-50"
                >
                  {form.submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Placing Order...
                    </span>
                  ) : product.stock === 0 ? 'Out of Stock' : 'Order Now →'}
                </button>
              </form>
            </div>
          </div>

          {/* Prev / Next between products */}
          {products.length > 1 && (
            <div className="flex items-center justify-between mt-4">
              <button
                onClick={() => goTo(activeIndex - 1)}
                disabled={activeIndex === 0}
                className="flex items-center gap-1.5 text-xs text-warmgray-500 hover:text-offblack transition-colors disabled:opacity-30"
              >
                <ChevronLeftIcon className="w-4 h-4" />
                Previous
              </button>
              <span className="text-xs text-warmgray-400">{activeIndex + 1} / {products.length}</span>
              <button
                onClick={() => goTo(activeIndex + 1)}
                disabled={activeIndex === products.length - 1}
                className="flex items-center gap-1.5 text-xs text-warmgray-500 hover:text-offblack transition-colors disabled:opacity-30"
              >
                Next
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && product && form && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-offblack/95"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 transition-colors"
            onClick={() => setLightboxOpen(false)}
            aria-label="Close"
          >
            <XMarkIcon className="h-6 w-6 text-white" />
          </button>

          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            {form.imageIndex + 1} / {product.images.length}
          </div>

          {product.images.length > 1 && (
            <>
              <button
                className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 transition-colors"
                onClick={(e) => { e.stopPropagation(); lightboxPrev(); }}
                aria-label="Previous"
              >
                <ChevronLeftIcon className="h-6 w-6 text-white" />
              </button>
              <button
                className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 transition-colors"
                onClick={(e) => { e.stopPropagation(); lightboxNext(); }}
                aria-label="Next"
              >
                <ChevronRightIcon className="h-6 w-6 text-white" />
              </button>
            </>
          )}

          <img
            src={getImageUrl(product.images[form.imageIndex])}
            alt={product.name}
            className="max-h-[85vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {product.images.length > 1 && (
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-[90vw] px-4"
              onClick={(e) => e.stopPropagation()}
            >
              {product.images.map((img, idx) => (
                <img
                  key={idx}
                  src={getImageUrl(img)}
                  alt={`${product.name} ${idx + 1}`}
                  onClick={() => updateForm(product._id, { imageIndex: idx })}
                  className={`h-14 w-14 object-cover shrink-0 cursor-pointer transition-all border-2 ${
                    idx === form.imageIndex
                      ? 'border-white opacity-100'
                      : 'border-transparent opacity-40 hover:opacity-80'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <footer className="bg-offblack text-warmgray-400 text-center py-8 text-xs tracking-wide mt-4">
        <p>© {new Date().getFullYear()} armé — Premium Jeans & Shoes</p>
        <p className="mt-1 text-warmgray-600">Delivery across all Algeria · Cash on Delivery</p>
      </footer>
    </div>
  );
};

export default AdsLanding;
